/**
 * The sessions the "When" field offers (1.M-2 deliverable 20): `sessions.list` paged (50 a page,
 * up to four pages) in the route's order, sessions without a number first, then the newest number
 * (DECISIONS 139, item 1.T(8)). The same map gives the Secrets page "since session <n>".
 */
import { sessionsList } from '@codex/contracts';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { api } from '../auth/client.js';
import {
  type SessionRow,
  sessionNumbers,
  sessionOptionLabel,
  sessionRows,
} from './sessions-model.js';

export const SESSIONS_LIMIT = 50;
export const SESSIONS_MAX_PAGES = 4;
export const sessionsKey = ['ledger', 'sessions'] as const;

export interface SessionOption {
  readonly id: string;
  readonly label: string;
  readonly title: string;
  readonly number: number | null;
}

export function useSessionOptions(enabled = true): {
  readonly options: readonly SessionOption[];
  readonly rows: readonly SessionRow[];
  readonly numbers: ReadonlyMap<string, number>;
  readonly isPending: boolean;
  readonly isError: boolean;
} {
  const query = useInfiniteQuery({
    queryKey: sessionsKey,
    queryFn: ({ pageParam }) =>
      api(sessionsList, {
        query: { limit: SESSIONS_LIMIT, ...(pageParam === null ? {} : { cursor: pageParam }) },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next,
    retry: false,
    enabled,
  });
  const loaded = query.data?.pages.length ?? 0;
  useEffect(() => {
    if (query.hasNextPage && !query.isFetchingNextPage && loaded < SESSIONS_MAX_PAGES) {
      void query.fetchNextPage();
    }
  }, [query, loaded]);
  const pages = query.data?.pages;
  const rows = useMemo(() => sessionRows((pages ?? []).map((page) => page.items)), [pages]);
  const options = useMemo(
    () =>
      rows.map((row) => ({
        id: row.id,
        label: sessionOptionLabel(row),
        title: row.title,
        number: row.number,
      })),
    [rows],
  );
  const numbers = useMemo(() => sessionNumbers(rows), [rows]);
  return { options, rows, numbers, isPending: query.isPending, isError: query.isError };
}
